import { useState } from "react";
import { Icon } from "../../kit";
import { useViewer } from "../../../state/ViewerProvider";
import { useEditorStore, useUi } from "../../../state/editor/useEditor";
import { beginSceneRestore } from "../../../state/editor/modelLoads";
import { isJsceneName, sniffSceneFile } from "../../../util/sceneFileSniff";
import { useCaps } from "../CapsProvider";
import { ZERO_INSET, type CardInset } from "../layout";
import { restoreSceneFile } from "../scene/sceneFile";

const ACCEPT = ".glb,.gltf,.obj,.fbx,.stl,.3dm,.vjson,.json";
/** v65 adds the whole-scene file. */
const ACCEPT_V65 = `${ACCEPT},.jscene`;

/** Centered drag-and-drop dropzone + a sample row, shown when no model is
 * loaded. Mirrors iJewel's "Drag and drop a 3D file here" empty state. */
export function EmptyState({ viewerOrigin, inset }: { viewerOrigin: string; inset?: CardInset }) {
  const { client } = useViewer();
  const r = client.ring;
  const store = useEditorStore();
  const caps = useCaps();
  const [ui] = useUi();
  const [hover, setHover] = useState(false);
  // Importing a model is the longest operation in the editor. Fire-and-forget
  // `void` left the dropzone sitting there unchanged for the whole parse, so a
  // user with a heavy GLB had no way to tell the drop had registered — and a
  // rejected import vanished into an unhandled rejection.
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const track = (p: Promise<unknown>) => {
    setBusy(true);
    setError(null);
    p.then(
      () => setBusy(false),
      (e) => { setBusy(false); setError(e instanceof Error ? e.message : "Import failed."); },
    );
  };

  /**
   * A saved scene — a whole-scene file (v65), or an older settings file. Either
   * can arrive with models (our of-vjson lists them, and the viewer reloads
   * them into an empty scene), and those come WITH a scene, so the "Apply
   * default materials" dialog must leave them alone.
   */
  const openScene = async (file: File) => {
    const restore = beginSceneRestore(client);
    try {
      // A whole scene goes through the studio's own open, so the configurator,
      // watermark and On Body in it come back too. No Undo step: there is no
      // piece yet, and the history starts over when one loads.
      if (caps.sceneFileV65 && (isJsceneName(file.name) || (await sniffSceneFile(file)))) await restoreSceneFile(client, store, file);
      else await r.loadVjsonFile(file);
      // This drop zone only shows on an empty scene: whatever is there now came with the document.
      restore.claim((await r.listModels()).map((m) => m.id));
    } finally {
      restore.end();
    }
  };

  /**
   * One 3D file. A "Ring + scene" file (v65) brings its own look: the restore
   * window opens BEFORE the model is added, so the materials dialog cannot
   * flash over it (see modelLoads.ts). A settings-only file with a 3D
   * extension is opened as a scene.
   */
  const openModel = async (file: File) => {
    const opts = ui.dropOptions;
    const sniff = caps.sceneFileV65 && /\.glb$/i.test(file.name) ? await sniffSceneFile(file) : null;
    if (sniff && !sniff.hasModel) return openScene(file);
    const restore = sniff ? beginSceneRestore(client) : null;
    try {
      const id = await r.addModelFile(file, opts);
      restore?.claim(id);
    } finally {
      restore?.end();
    }
  };

  const loadFiles = (files: File[]) => {
    if (files.length === 0 || busy) return;
    const scene = files.find((f) => /\.(vjson|json)$/i.test(f.name) || (caps.sceneFileV65 && isJsceneName(f.name)));
    if (scene) return track(openScene(scene));
    // Multiple files (e.g. OBJ + MTL + textures) → multi-file import; else single.
    track(files.length > 1 ? Promise.resolve(r.addModelFiles(files, ui.dropOptions)) : openModel(files[0]));
  };

  const samples = [
    { id: "solitaire", label: "Solitaire", icon: "diamond", url: `${viewerOrigin}/models/Ajaffe-dummy.glb` },
  ];

  return (
    <div className="pointer-events-auto absolute z-[5] flex items-center justify-center" style={inset ?? ZERO_INSET}>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setHover(true);
        }}
        onDragLeave={() => setHover(false)}
        onDrop={(e) => {
          e.preventDefault();
          setHover(false);
          const files = e.dataTransfer?.files ? Array.from(e.dataTransfer.files) : [];
          if (files.length) loadFiles(files);
        }}
        className={`flex w-[420px] max-w-[80%] flex-col items-center gap-4 rounded-2xl border-2 border-dashed bg-ed-panel/80 px-8 py-10 text-center backdrop-blur-sm transition-colors ${
          hover ? "border-ed-accent" : "border-ed-border"
        }`}
      >
        <Icon
          name={busy ? "progress_activity" : "upload_file"}
          size={36}
          className={busy ? "animate-spin text-ed-accent" : "text-ed-text-3"}
        />
        <div>
          <p className="text-[15px] font-semibold text-ed-text">
            {busy ? "Opening your piece…" : "Drop your ring file here"}
          </p>
          <p className="mt-1 text-[13px] text-ed-text-2">Rhino, STL, OBJ, FBX and other 3D files — straight from your CAD</p>
          {error ? <p className="mt-1.5 text-[12px] text-ed-red">{error}</p> : null}
        </div>
        <button
          disabled={busy}
          onClick={() => {
            const inp = document.createElement("input");
            inp.type = "file";
            inp.accept = caps.sceneFileV65 ? ACCEPT_V65 : ACCEPT;
            inp.multiple = true;
            inp.onchange = () => inp.files && loadFiles(Array.from(inp.files));
            inp.click();
          }}
          className="rounded-lg bg-ed-ink px-4 py-2 text-[13px] font-semibold text-white hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
        >
          Choose a file
        </button>
        <div className="mt-2 flex flex-col items-center gap-2">
          <span className="text-[12px] font-semibold text-ed-text-2">Or try a sample ring</span>
          <div className="flex gap-2">
            {samples.map((s) => (
              <button
                key={s.id}
                disabled={busy}
                onClick={() => track(Promise.resolve(r.addModelUrl(s.url)))}
                className="flex flex-col items-center gap-1 rounded-xl border border-ed-border bg-ed-panel px-4 py-3 hover:border-ed-line hover:bg-ed-hover disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Icon name={s.icon} size={22} className="text-ed-gold" />
                <span className="text-[12px] text-ed-text">{s.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
